import React, { useState } from 'react';
import { modulisticaAPI } from '../../services/modulisticaApi';
import FirmaCanvas from './FirmaCanvas';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/ModulisticaWizard.css';
import '../../styles/ModuloAnagraficoConsole.css';
import '../../styles/ModulisticaWizardConsole.css';
import { notificaErrore, notificaAvviso, notificaConferma, messaggioDa } from '../../utils/notifiche';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';

/**
 * CompletamentoConsensi
 * Per un paziente accettato "solo prelievi": raccoglie i due consensi
 * mancanti — consenso alla cura (privacy integrativo) e consenso immagini —
 * con firma separata. L'anagrafica e' gia' nel sistema e viene mostrata in
 * sola lettura. Al termine il paziente diventa "completo".
 *
 * Props:
 *  - paziente   record completo del paziente
 *  - onClose()
 *  - onSuccess(tipo_accettazione)
 */

const SceltaConsenso = ({ valore, onChange }) => (
    <div className="scelta-consenso">
        <label className={`scelta ${valore === true ? 'attiva si' : ''}`}>
            <input type="radio" checked={valore === true} onChange={() => onChange(true)} />
            <span>Acconsento</span>
        </label>
        <label className={`scelta ${valore === false ? 'attiva no' : ''}`}>
            <input type="radio" checked={valore === false} onChange={() => onChange(false)} />
            <span>Non acconsento</span>
        </label>
    </div>
);

const VoceConsenso = ({ titolo, descrizione, valore, onChange, errore, children }) => (
    <div className={`voce-consenso ${errore ? 'errore' : ''}`}>
        <h4>{titolo}</h4>
        {descrizione && <p className="voce-descrizione">{descrizione}</p>}
        <SceltaConsenso valore={valore} onChange={onChange} />
        {children}
        {errore && <span className="messaggio-errore">{errore}</span>}
    </div>
);

const CompletamentoConsensi = ({ paziente, onClose, onSuccess }) => {
    const [passo, setPasso] = useState(1); // 1 = cura/privacy, 2 = foto
    const [cura, setCura] = useState(null);
    const [foto, setFoto] = useState({ clinica: null, didattica: null, promozionale: null, modalita: 'volto_oscurato' });
    const [firmaPrivacy, setFirmaPrivacy] = useState(null);
    const [firmaFoto, setFirmaFoto] = useState(null);
    const [errori, setErrori] = useState({});
    const [invio, setInvio] = useState(false);

    /* Scelte e firme si dichiarano, come in ModulisticaWizard: al passo 2 la
       scelta e la firma del passo 1 vivono solo qui, i loro campi non sono
       piu' nel documento. Senza, «Annulla», il tasto indietro o la ricarica
       automatica della versione nuova (utils/versioneInLinea.js) le
       buttavano via senza una parola, e il paziente doveva firmare di nuovo
       (revisione del 2/10). L'istantanea si prende al montaggio: la finestra
       nasce vuota. */
    const sporco = useSporco({ cura, foto, firmaPrivacy, firmaFoto }, 0);
    const chiedi = useUscitaProtetta(sporco);
    const annulla = () => { if (chiedi()) onClose(); };

    const pulisci = (k) => setErrori((e) => { const n = { ...e }; delete n[k]; return n; });

    const validaPasso1 = () => {
        const e = {};
        if (cura === null) e.cura = 'Esprimi una scelta';
        else if (cura === false) e.cura = 'Senza questo consenso la prestazione sanitaria non puo essere erogata';
        if (!firmaPrivacy) e.firma_privacy = 'Apponi la firma nel riquadro';
        return e;
    };

    const validaPasso2 = () => {
        const e = {};
        if (foto.clinica === null) e.clinica = 'Esprimi una scelta';
        if (foto.didattica === null) e.didattica = 'Esprimi una scelta';
        if (foto.promozionale === null) e.promozionale = 'Esprimi una scelta';
        if (!firmaFoto) e.firma_foto = 'Apponi la firma nel riquadro';
        return e;
    };

    const avanti = () => {
        const e = validaPasso1();
        setErrori(e);
        if (Object.keys(e).length) return;
        setPasso(2);
    };

    const conferma = async () => {
        const e = validaPasso2();
        setErrori(e);
        if (Object.keys(e).length) return;

        const payload = {
            consensi: {
                privacy: { cura },
                foto: {
                    clinica: foto.clinica,
                    didattica: foto.didattica,
                    promozionale: foto.promozionale,
                    modalita: foto.promozionale ? foto.modalita : null
                }
            },
            firme: { privacy: firmaPrivacy, foto: firmaFoto },
            // L'ora della firma la decide il server (revisione del 7/10/2026).
            user_agent: navigator.userAgent
        };

        try {
            setInvio(true);
            const r = await modulisticaAPI.completaConsensi(paziente.id, payload);
            const { falliti = [], tipo_accettazione } = r.data.data;

            (falliti.length ? notificaAvviso : notificaConferma)(
                falliti.length
                    ? `Completamento parziale. Moduli non archiviati: ${falliti.join(', ')}.`
                    : `Consensi completati per ${paziente.cognome} ${paziente.nome}. Ora è un paziente regolare.`
            );

            if (onSuccess) onSuccess(tipo_accettazione);
            onClose();
        } catch (errore) {
            console.error('Errore completamento consensi:', errore);
            notificaErrore(messaggioDa(errore, 'Errore nel completamento dei consensi'));
        } finally {
            setInvio(false);
        }
    };

    return (
        <div className="modulo-overlay">
            <div className="modulo-finestra" role="dialog" aria-modal="true">
                <div className="modulo-body">
                    <header className="modulo-intestazione">
                        <div className="intestazione-ragione">RIMEDICAL S.r.l.</div>
                        <div className="intestazione-righe">
                            Via Europa 8 – 47822 Santarcangelo di Romagna (RN)<br />
                            Tel. 0541 620757 – Cell. 352 0453011<br />
                            P.IVA 04538930407 – Cod. Univoco KRRH6B9
                        </div>
                    </header>

                    <div className="fc-avanzamento">
                        Completamento consensi — {passo} di 2
                    </div>

                    <h2 className="modulo-titolo">
                        {passo === 1 ? 'Consenso al trattamento per finalità di cura' : 'Consenso immagini'}
                    </h2>

                    <section className="modulo-sezione">
                        <div className="riepilogo-paziente">
                            <div className="riepilogo-nome">{paziente.cognome} {paziente.nome}</div>
                            <div className="riepilogo-dettagli">
                                nato/a a {paziente.luogo_nascita || '—'} il {paziente.data_nascita ? new Date(paziente.data_nascita).toLocaleDateString('it-IT') : '—'}
                                {' '}— C.F. {paziente.codice_fiscale}
                            </div>
                            <span className="riepilogo-nota">Paziente già registrato al punto prelievi</span>
                        </div>
                    </section>

                    {passo === 1 && (
                        <>
                            <section className="modulo-sezione">
                                <VoceConsenso
                                    titolo="Trattamento dei dati sulla salute per diagnosi, cura e cartella clinica"
                                    descrizione="Necessario per erogare la prestazione sanitaria."
                                    valore={cura}
                                    onChange={(v) => { setCura(v); pulisci('cura'); }}
                                    errore={errori.cura}
                                />
                            </section>
                            <section className="modulo-sezione">
                                <h3>Firma del paziente</h3>
                                <FirmaCanvas valore={firmaPrivacy}
                                    onChange={(v) => { setFirmaPrivacy(v); pulisci('firma_privacy'); }}
                                    errore={errori.firma_privacy} />
                            </section>
                        </>
                    )}

                    {passo === 2 && (
                        <>
                            <section className="modulo-sezione">
                                <VoceConsenso
                                    titolo="A. Riprese fotografiche per la cartella clinica"
                                    valore={foto.clinica}
                                    onChange={(v) => { setFoto((f) => ({ ...f, clinica: v })); pulisci('clinica'); }}
                                    errore={errori.clinica}
                                />
                                <VoceConsenso
                                    titolo="B. Utilizzo a scopo didattico e formativo"
                                    valore={foto.didattica}
                                    onChange={(v) => { setFoto((f) => ({ ...f, didattica: v })); pulisci('didattica'); }}
                                    errore={errori.didattica}
                                />
                                <VoceConsenso
                                    titolo="C. Utilizzo a scopo promozionale e divulgativo"
                                    valore={foto.promozionale}
                                    onChange={(v) => { setFoto((f) => ({ ...f, promozionale: v })); pulisci('promozionale'); }}
                                    errore={errori.promozionale}
                                >
                                    {foto.promozionale === true && (
                                        <div className="griglia sotto-campi">
                                            <label className="riga-radio">
                                                <input type="radio" checked={foto.modalita === 'volto_oscurato'}
                                                    onChange={() => setFoto((f) => ({ ...f, modalita: 'volto_oscurato' }))} />
                                                <span>Volto oscurato</span>
                                            </label>
                                            <label className="riga-radio">
                                                <input type="radio" checked={foto.modalita === 'volto_visibile'}
                                                    onChange={() => setFoto((f) => ({ ...f, modalita: 'volto_visibile' }))} />
                                                <span>Volto visibile</span>
                                            </label>
                                        </div>
                                    )}
                                </VoceConsenso>
                            </section>
                            <section className="modulo-sezione">
                                <h3>Firma del paziente</h3>
                                <FirmaCanvas valore={firmaFoto}
                                    onChange={(v) => { setFirmaFoto(v); pulisci('firma_foto'); }}
                                    errore={errori.firma_foto} />
                            </section>
                        </>
                    )}
                </div>

                <footer className="modulo-footer">
                    {passo === 2 && (
                        <button className="btn-annulla" onClick={() => { setErrori({}); setPasso(1); }} disabled={invio}>
                            Indietro
                        </button>
                    )}
                    <button className="btn-annulla" onClick={annulla} disabled={invio}>Annulla</button>
                    {passo === 1 ? (
                        <button className="btn-conferma" onClick={avanti}>Avanti</button>
                    ) : (
                        <button className="btn-conferma" onClick={conferma} disabled={invio}>
                            {invio ? 'Salvataggio…' : 'Completa e archivia'}
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default CompletamentoConsensi;
